import clsx from "clsx";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, {
  DetailedHTMLProps,
  FC,
  HTMLAttributes,
  PropsWithChildren,
  useCallback,
} from "react";

import { Cta } from "../../buttons/cta";
import { RichText } from "../../rich-text";
import { ContactCardColor } from "./types";

export interface ContactCardProps
  extends Queries.ContactCardDataFragment,
    Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "className"> {
  onClickCta?: (i: number) => void;
}

export const ContactCard: FC<PropsWithChildren<ContactCardProps>> = ({
  body,
  color,
  logo,
  ctas,
  className,
  onClickCta,
  children,
}) => {
  const getCtasVariant = useCallback(
    (index: number) => {
      const firstCardVariant = color === ContactCardColor.LIGHT ? "primary" : "light";
      const secondCardVariant =
        color === ContactCardColor.LIGHT ? "primary-outline" : "light-outline";
      return !index ? firstCardVariant : secondCardVariant;
    },
    [color]
  );

  return (
    <div className={clsx(["rounded-3xl overflow-hidden flex flex-col", className])}>
      {logo?.gatsbyImageData && (
        <div className="w-full p-1 flex justify-center items-center bg-light h-28 flex-none">
          <GatsbyImage
            image={logo.gatsbyImageData}
            alt={logo.title ?? ""}
            objectFit="contain"
            className="h-full"
          />
        </div>
      )}
      <div
        className={clsx([
          "flex flex-col gap-4 p-5 pt-6 justify-between contact-card h-full",
          color === ContactCardColor.LIGHT ? "bg-gray-100" : "bg-primary",
        ])}
      >
        {body?.raw ? (
          <RichText
            raw={body?.raw}
            className={clsx([
              "text-base leading-snug font-light break-words",
              "[&_h6]:mb-1 [&_h5]:mb-4 [&_p]:mb-2",
              "[&_b]:font-medium [&_h6]:font-medium [&_h5]:font-medium",
              color === ContactCardColor.LIGHT ? "text-dark" : "text-light",
              color === ContactCardColor.LIGHT && "[&_h5]:text-primary [&_h6]:text-primary",
            ])}
          />
        ) : (
          children
        )}
        <div className="flex flex-col gap-3">
          {ctas
            ?.filter(Boolean)
            .map((cta, i) => (
              <Cta
                key={i}
                onClick={() => onClickCta?.(i)}
                variant={getCtasVariant(i)}
                size="sm"
                {...cta!}
                fullWidth
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export const query = graphql`
  fragment ContactCardData on ContentfulContactCard {
    body {
      raw
    }
    logo {
      gatsbyImageData
      title
    }
    color
    ctas {
      ...CtaData
    }
  }
`;
